<template>
  <a-layout>
    <a-layout-content class="announcement-detail-editor">
      <div id="toolbar">
        <select class="ql-size">
          <option value="small" />
          <!-- Note a missing, thus falsy value, is used to reset to default -->
          <option selected />
          <option value="large" />
          <option value="huge" />
        </select>

        <!-- Add buttons as you would before -->
        <button class="ql-bold" />
        <button class="ql-italic" />

        <button class="ql-link" />

        <select class="ql-color" />
        <color-picker
          :value="customBackgroundColor"
          @change="(color) => handleChangeBackgroundColor(color, true)">
          <template v-slot:icon>
            <a-icon type="bg-colors" />
          </template>
        </color-picker>
      </div>
      <quill-editor
        :content="content"
        :options="editorOption"
        ref="myQuillEditor"
        @change="handleEditorChange($event)"
      />
      <div>
        <span>
          {{ 'announcement-editor.current-length' | globalize }}: {{ contentLength }}
        </span>
        <span class="pull-right">
          {{ 'announcement-editor.max-length' | globalize }}: {{ contentLengthLimit }}
        </span>
      </div>
    </a-layout-content>
  </a-layout>
</template>

<script>
import AIcon from 'ant-design-vue/lib/icon'
import ALayout from 'ant-design-vue/lib/layout'
import message from 'ant-design-vue/lib/message'
import Emitter from 'quill/core/emitter'
import { quillEditor } from 'vue-quill-editor'
import ColorPicker from '@/vue/fields/ColorPicker'

/*
*
* bg color, text color, add text, add link,
* add page,
* visibility time,
* and then schedule the announcement visibility on the platform
*
* */

function SnowTooltipSave () {
  const { value } = this.textbox

  const linkValidityRegex = /^(http|https)/
  switch (this.root.getAttribute('data-mode')) {
    case 'link': {
      if (!linkValidityRegex.test(value)) {
        message.error('Link should start from http(s)://...')
        return
      }
      const { scrollTop } = this.quill.root
      if (this.linkRange) {
        this.quill.formatText(this.linkRange, 'link', value, Emitter.sources.USER)
        delete this.linkRange
      } else {
        this.restoreFocus()
        this.quill.format('link', value, Emitter.sources.USER)
      }
      this.quill.root.scrollTop = scrollTop
      break
    }
    default:
  }
  this.textbox.value = ''
  this.hide()
}

function ValidateLink (value) {
  if (value) {
    const range = this.quill.getSelection()
    if (range == null || range.length === 0) return
    let preview = this.quill.getText(range)

    if (/^\S+@\S+\.\S+$/.test(preview) && preview.indexOf('mailto:') !== 0) {
      preview = 'mailto:' + preview
    }
    const tooltip = this.quill.theme.tooltip

    tooltip.edit('link', preview)
  } else {
    this.quill.format('link', false)
  }
}

export default {
  name: 'announcement-editor',
  components: {
    ALayout,
    'a-layout-content': ALayout.Content,
    quillEditor,
    AIcon,
    ColorPicker,
  },
  props: {
    customBackgroundColor: {
      type: String,
      default: '',
    },
    editableText: {
      type: String,
      default: '',
    },
    onTextChanged: {
      type: Function,
      default: () => {},
    },
    onBackgroundColorChanged: {
      type: Function,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
  },
  data () {
    return {
      showTextColorPicker: true,
      backgroundColor: null,
      content: this.editableText,
      contentLengthLimit: 200,
      contentLength: 0,
      colors: '#194d33',
      editorOption: {
        modules: {
          toolbar: {
            container: '#toolbar',
            handlers: {
              link: ValidateLink,
            },
          },
        },
      },
    }
  },
  computed: {
    editor () {
      const result = this.$refs.myQuillEditor ? this.$refs.myQuillEditor.quill : null
      if (this.disabled && result) {
        result.disable()
      }
      return result
    },
  },
  mounted () {
    if (this.customBackgroundColor) {
      this.handleChangeBackgroundColor(this.customBackgroundColor)
    }
    const { contentLengthLimit } = this
    const self = this
    const { quill } = this.$refs.myQuillEditor

    this.contentLength = quill.getLength()

    quill.theme.tooltip.save = SnowTooltipSave

    quill.on('text-change', (delta, old, source) => {
      const length = quill.getLength()
      self.contentLength = length

      if (length > contentLengthLimit) {
        quill.deleteText(contentLengthLimit, quill.getLength())
      }
    })
  },
  methods: {
    handleEditorChange ({ html }) {
      this.content = html

      if (this.onTextChanged) {
        this.onTextChanged(html)
      }
    },

    handleChangeBackgroundColor (color, setSelection = false) {
      if (setSelection) {
        const txt = this.editor.getText()
        this.editor.setSelection(0, txt.length)
      }
      this.backgroundColor = color
      this.editor.format('background', color)
      this.editor.container.children[0].style = `background: ${color};`
      this.onBackgroundColorChanged(color)
      this.editor.focus()
    },

    handleChangeTextColor (color) {
      this.editor.format('color', color)
    },
  },
}
</script>

<style scoped>
  @import '~ant-design-vue/lib/icon/style/index.css';
  @import '~ant-design-vue/lib/layout/style/index.css';
  @import '~ant-design-vue/lib/message/style/index.css';
  @import '~quill/dist/quill.core.css';
  @import '~quill/dist/quill.snow.css';
  @import '~quill/dist/quill.bubble.css';

  #toolbar {
    width: 100%;
    display: table-cell;
    margin: 0;
    padding: 0.625em;
  }

  .announcement-detail-editor > * {
    background: $_white;
  }
</style>
